import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';

const DIAS_SEMANA = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function pad(n) { return n.toString().padStart(2, '0'); }
function fechaISO(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function lunesDeSemana(d) {
  const dia = d.getDay(); // 0=domingo
  const diff = dia === 0 ? -6 : 1 - dia;
  const l = new Date(d);
  l.setDate(d.getDate() + diff);
  l.setHours(0, 0, 0, 0);
  return l;
}
function sumarDias(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function hhmm(t) { return t ? t.slice(0, 5) : ''; }

export default function Guardias({ miRol, miAgente }) {
  const isMobile = useIsMobile();
  const [desarrollos, setDesarrollos] = useState([]);
  const [agentesTodos, setAgentesTodos] = useState([]);
  const [guardias, setGuardias] = useState({}); // key: desarrolloId_fechaISO_turno -> { id, hora_inicio, hora_fin, asesores: [{correo,nombre}] }
  const [loading, setLoading] = useState(true);
  const [inicioSemana, setInicioSemana] = useState(lunesDeSemana(new Date()));
  const [editando, setEditando] = useState(null); // { desarrolloId, desarrolloNombre, fecha, turno }
  const [formEdit, setFormEdit] = useState({ hora_inicio: '', hora_fin: '', asesor1: '', asesor2: '' });
  const [guardando, setGuardando] = useState(false);
  const [msgError, setMsgError] = useState('');

  const esAdminGeneral = miRol === 'Super Admin' || miRol === 'Admin' || miRol === 'Sub Admin';
  const diasSemana = Array.from({ length: 7 }, (_, i) => sumarDias(inicioSemana, i));

  useEffect(() => { cargarDesarrollos(); cargarAgentes(); }, []);

  const cargarDesarrollos = async () => {
    setLoading(true);
    let query = supabase.from('desarrollos').select('id, nombre').order('nombre');
    if (!esAdminGeneral) {
      const asignados = miAgente?.desarrollos_cargo || [];
      if (asignados.length === 0) { setDesarrollos([]); setLoading(false); return; }
      query = query.in('nombre', asignados);
    }
    const { data } = await query;
    setDesarrollos(data || []);
    setLoading(false);
  };

  const cargarAgentes = async () => {
    const { data } = await supabase.from('agentes').select('correo, nombre, apellidos, desarrollos').eq('rol', 'Agente').eq('activo', true);
    setAgentesTodos(data || []);
  };

  const cargarGuardias = useCallback(async () => {
    const ids = desarrollos.map(d => d.id);
    if (ids.length === 0) { setGuardias({}); return; }
    const inicio = fechaISO(diasSemana[0]);
    const fin = fechaISO(diasSemana[6]);
    const { data } = await supabase
      .from('guardias')
      .select('id, desarrollo_id, fecha, turno, hora_inicio, hora_fin, guardia_asesores(id, agente_correo, orden)')
      .in('desarrollo_id', ids)
      .gte('fecha', inicio)
      .lte('fecha', fin);

    const mapa = {};
    (data || []).forEach(g => {
      const asesores = (g.guardia_asesores || [])
        .sort((a, b) => a.orden - b.orden)
        .map(ga => {
          const ag = agentesTodos.find(a => a.correo === ga.agente_correo);
          return { correo: ga.agente_correo, nombre: ag ? `${ag.nombre} ${ag.apellidos || ''}`.trim() : ga.agente_correo };
        });
      const key = `${g.desarrollo_id}_${g.fecha}_${g.turno}`;
      mapa[key] = { id: g.id, hora_inicio: g.hora_inicio, hora_fin: g.hora_fin, asesores };
    });
    setGuardias(mapa);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desarrollos, inicioSemana, agentesTodos]);

  useEffect(() => { if (desarrollos.length > 0) cargarGuardias(); }, [desarrollos, inicioSemana, agentesTodos, cargarGuardias]);

  const agentesDelDesarrollo = (nombreDesarrollo) =>
    agentesTodos.filter(a => (a.desarrollos || []).includes(nombreDesarrollo));

  const abrirEditor = (desarrollo, fecha, turno) => {
    const key = `${desarrollo.id}_${fechaISO(fecha)}_${turno}`;
    const existente = guardias[key];
    const defaultHoraInicio = turno === 'AM' ? '09:30' : '14:00';
    const defaultHoraFin = turno === 'AM' ? '14:00' : '19:00';
    setFormEdit({
      hora_inicio: existente ? hhmm(existente.hora_inicio) : defaultHoraInicio,
      hora_fin: existente ? hhmm(existente.hora_fin) : defaultHoraFin,
      asesor1: existente?.asesores?.[0]?.correo || '',
      asesor2: existente?.asesores?.[1]?.correo || '',
    });
    setMsgError('');
    setEditando({ desarrolloId: desarrollo.id, desarrolloNombre: desarrollo.nombre, fecha, turno, guardiaId: existente?.id || null });
  };

  const cerrarEditor = () => { setEditando(null); setMsgError(''); };

  const handleGuardarGuardia = async () => {
    if (!formEdit.hora_inicio || !formEdit.hora_fin) { setMsgError('Captura hora de inicio y fin'); return; }
    if (formEdit.asesor1 && formEdit.asesor2 && formEdit.asesor1 === formEdit.asesor2) { setMsgError('No puedes repetir el mismo asesor'); return; }
    setGuardando(true);
    setMsgError('');

    const { data: guardiaData, error } = await supabase
      .from('guardias')
      .upsert({
        desarrollo_id: editando.desarrolloId,
        fecha: fechaISO(editando.fecha),
        turno: editando.turno,
        hora_inicio: formEdit.hora_inicio,
        hora_fin: formEdit.hora_fin,
        creado_por: miAgente?.correo || null,
      }, { onConflict: 'desarrollo_id,fecha,turno' })
      .select()
      .single();

    if (error) { setMsgError('Error al guardar: ' + error.message); setGuardando(false); return; }

    await supabase.from('guardia_asesores').delete().eq('guardia_id', guardiaData.id);
    const nuevosAsesores = [];
    if (formEdit.asesor1) nuevosAsesores.push({ guardia_id: guardiaData.id, agente_correo: formEdit.asesor1, orden: 0 });
    if (formEdit.asesor2) nuevosAsesores.push({ guardia_id: guardiaData.id, agente_correo: formEdit.asesor2, orden: 1 });
    if (nuevosAsesores.length > 0) await supabase.from('guardia_asesores').insert(nuevosAsesores);

    setGuardando(false);
    setEditando(null);
    cargarGuardias();
  };

  const handleEliminarGuardia = async () => {
    if (!editando?.guardiaId) { setEditando(null); return; }
    if (!window.confirm('¿Eliminar esta guardia?')) return;
    setGuardando(true);
    await supabase.from('guardias').delete().eq('id', editando.guardiaId);
    setGuardando(false);
    setEditando(null);
    cargarGuardias();
  };

  const celda = (desarrollo, fecha, turno) => {
    const key = `${desarrollo.id}_${fechaISO(fecha)}_${turno}`;
    const g = guardias[key];
    return (
      <td key={turno} onClick={() => abrirEditor(desarrollo, fecha, turno)}
        style={{ padding: '8px 10px', border: '0.5px solid #eee', cursor: 'pointer', verticalAlign: 'top', minWidth: '110px', background: g ? '#fff' : '#fafafa' }}>
        {g ? (
          <div>
            {g.asesores.length === 0 ? (
              <div style={{ fontSize: '11px', color: '#bbb' }}>Sin asesor</div>
            ) : g.asesores.map((a, i) => (
              <div key={i} style={{ fontSize: '12px', fontWeight: '500', color: '#333' }}>{a.nombre}</div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: '11px', color: '#ccc', textAlign: 'center' }}>+ Agregar</div>
        )}
      </td>
    );
  };

  const nombreCompleto = (a) => `${a.nombre} ${a.apellidos || ''}`.trim();

  // Horario representativo del turno para esa semana: toma el primer día
  // de la semana que tenga guardia cargada para ese desarrollo/turno.
  // Las celdas de cada día pueden tener horas distintas (se respeta al
  // editar), pero aquí solo mostramos una referencia rápida junto a AM/PM.
  const horaTurno = (desarrollo, turno) => {
    for (const fecha of diasSemana) {
      const key = `${desarrollo.id}_${fechaISO(fecha)}_${turno}`;
      const g = guardias[key];
      if (g) return `${hhmm(g.hora_inicio)} - ${hhmm(g.hora_fin)}`;
    }
    return null;
  };

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: isMobile ? '17px' : '20px', fontWeight: '500', color: '#1a1a2e', marginBottom: '4px' }}>Guardias</h2>
          <div style={{ fontSize: '13px', color: '#888' }}>Carga semanal de turnos por desarrollo</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <button onClick={() => setInicioSemana(sumarDias(inicioSemana, -7))}
            style={{ padding: '8px 14px', border: '0.5px solid #ddd', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>‹ Semana anterior</button>
          <input type='date' value={fechaISO(inicioSemana)} onChange={e => setInicioSemana(lunesDeSemana(new Date(e.target.value + 'T00:00:00')))}
            style={{ padding: '8px 10px', border: '0.5px solid #ddd', borderRadius: '8px', fontSize: '13px' }} />
          <button onClick={() => setInicioSemana(sumarDias(inicioSemana, 7))}
            style={{ padding: '8px 14px', border: '0.5px solid #ddd', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>Semana siguiente ›</button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>Cargando...</div>
      ) : desarrollos.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>
          {esAdminGeneral ? 'Sin desarrollos' : 'No tienes desarrollos asignados para cargar guardias'}
        </div>
      ) : (
        <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#1a1a2e', color: '#fff' }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '500', minWidth: '160px' }}>Desarrollo</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '500', minWidth: '70px' }}>Turno</th>
                {diasSemana.map((d, i) => (
                  <th key={i} style={{ padding: '10px 10px', textAlign: 'left', fontWeight: '500', minWidth: '110px' }}>
                    {DIAS_SEMANA[d.getDay()].toUpperCase()}<br />
                    <span style={{ fontWeight: '400', fontSize: '11px', opacity: 0.8 }}>{d.getDate()}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {desarrollos.map(desarrollo => (
                <React.Fragment key={desarrollo.id}>
                  <tr>
                    <td rowSpan={2} style={{ padding: '10px 14px', border: '0.5px solid #eee', fontWeight: '600', color: '#1a1a2e', verticalAlign: 'top', background: '#fafafa' }}>
                      {desarrollo.nombre}
                    </td>
                    <td style={{ padding: '8px 14px', border: '0.5px solid #eee', color: '#666', fontSize: '12px', fontWeight: '500' }}>
                      AM{horaTurno(desarrollo, 'AM') && <span style={{ color: '#aaa', fontWeight: '400', marginLeft: '4px' }}>{horaTurno(desarrollo, 'AM')}</span>}
                    </td>
                    {diasSemana.map(fecha => celda(desarrollo, fecha, 'AM'))}
                  </tr>
                  <tr>
                    <td style={{ padding: '8px 14px', border: '0.5px solid #eee', color: '#666', fontSize: '12px', fontWeight: '500' }}>
                      PM{horaTurno(desarrollo, 'PM') && <span style={{ color: '#aaa', fontWeight: '400', marginLeft: '4px' }}>{horaTurno(desarrollo, 'PM')}</span>}
                    </td>
                    {diasSemana.map(fecha => celda(desarrollo, fecha, 'PM'))}
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editando && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={cerrarEditor}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '380px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '600', margin: 0, color: '#1a1a2e' }}>{editando.desarrolloNombre}</h3>
              <button onClick={cerrarEditor} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#888' }}>×</button>
            </div>
            <div style={{ fontSize: '12px', color: '#888', marginBottom: '1.25rem' }}>
              {DIAS_SEMANA[editando.fecha.getDay()]} {editando.fecha.getDate()} · Turno {editando.turno}
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' }}>Hora inicio</label>
                <input type='time' value={formEdit.hora_inicio} onChange={e => setFormEdit({ ...formEdit, hora_inicio: e.target.value })}
                  style={{ width: '100%', padding: '8px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' }}>Hora fin</label>
                <input type='time' value={formEdit.hora_fin} onChange={e => setFormEdit({ ...formEdit, hora_fin: e.target.value })}
                  style={{ width: '100%', padding: '8px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' }}>Asesor 1</label>
              <select value={formEdit.asesor1} onChange={e => setFormEdit({ ...formEdit, asesor1: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', background: '#fff' }}>
                <option value=''>— Ninguno —</option>
                {agentesDelDesarrollo(editando.desarrolloNombre).map(a => (
                  <option key={a.correo} value={a.correo}>{nombreCompleto(a)}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '18px' }}>
              <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' }}>Asesor 2 (opcional)</label>
              <select value={formEdit.asesor2} onChange={e => setFormEdit({ ...formEdit, asesor2: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', background: '#fff' }}>
                <option value=''>— Ninguno —</option>
                {agentesDelDesarrollo(editando.desarrolloNombre).map(a => (
                  <option key={a.correo} value={a.correo}>{nombreCompleto(a)}</option>
                ))}
              </select>
            </div>

            {msgError && (
              <div style={{ padding: '10px', background: '#FCEBEB', color: '#A32D2D', borderRadius: '8px', fontSize: '12px', marginBottom: '14px' }}>{msgError}</div>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              {editando.guardiaId && (
                <button onClick={handleEliminarGuardia} disabled={guardando}
                  style={{ padding: '10px 16px', background: '#FCEBEB', color: '#A32D2D', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  Eliminar
                </button>
              )}
              <button onClick={handleGuardarGuardia} disabled={guardando}
                style={{ flex: 1, padding: '10px 16px', background: '#4A4A4A', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                {guardando ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}